import React, { useCallback, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { Database, Package2, Building2, RefreshCw, Save, AlertCircle } from "lucide-react";
import { auth, db } from "../firebase";
import { canViewGrids, normalizeGridsVisibility } from "../utils/gridsAccess";
import {
  buildInventoryPriceChangeDetail,
  createInventoryPriceChangeWrite,
} from "../utils/inventoryPriceChanges";

const ACCOUNT_STAGES = ["Prospect", "Sampling", "Customer", "Churn Risk", "Inactive"];

const normalizeRole = (value) => String(value || "").trim().toLowerCase();
const uniqueStrings = (values) => Array.from(new Set((Array.isArray(values) ? values : []).map((value) => String(value || "").trim()).filter(Boolean)));
const normalizeSku = (value) => String(value || "").trim().toLowerCase();
const batchInChunks = (items, size = 350) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const InventoryCell = ({ value, onChange, type = "text", min, step, readOnly = false }) => (
  <input
    type={type}
    min={min}
    step={step}
    value={value}
    readOnly={readOnly}
    onChange={(event) => onChange?.(event.target.value)}
    className={`h-9 w-full rounded-md border border-slate-200 bg-white px-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${
      readOnly ? "cursor-default bg-slate-50 text-slate-500" : ""
    }`}
  />
);

const CheckboxCell = ({ checked, onChange, disabled = false }) => (
  <label className={`flex h-9 items-center justify-center ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
    <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(event) => onChange?.(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
  </label>
);

const SelectCell = ({ value, onChange, options, disabled = false }) => (
  <select
    value={value}
    disabled={disabled}
    onChange={(event) => onChange?.(event.target.value)}
    className={`h-9 w-full rounded-md border border-slate-200 bg-white px-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${
      disabled ? "cursor-not-allowed bg-slate-50 text-slate-500" : ""
    }`}
  >
    <option value="">None</option>
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

const GridShell = ({ title, icon: Icon, description, search, onSearchChange, children, emptyText, rightActions = null }) => (
  <div className="space-y-4">
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {title ? (
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
              <Icon className="h-4 w-4" />
            </div>
          ) : null}
          <div>
            {title ? <h2 className="text-sm font-semibold text-slate-900">{title}</h2> : null}
            {description ? <p className="text-xs text-slate-500">{description}</p> : null}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:justify-end">
        <div className="w-full md:w-72">
          <input
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={`Search ${(title || "grid").toLowerCase()}...`}
            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
        {rightActions}
      </div>
    </div>

    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {React.Children.count(children) ? children : <div className="px-4 py-10 text-sm text-slate-500">{emptyText}</div>}
    </div>
  </div>
);

export default function GridsScreen({
  initialTab = "inventory",
  forcedTab = null,
  showTabNav = true,
  embedded = false,
  onInventorySaved = null,
  inventoryItems = null,
}) {
  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [companySettings, setCompanySettings] = useState({ featureFlags: {} });
  const [teamUsers, setTeamUsers] = useState([]);
  const [territories, setTerritories] = useState([]);

  const [activeTab, setActiveTab] = useState(forcedTab || initialTab || "inventory");
  const [loadingInventory, setLoadingInventory] = useState(true);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [inventoryRows, setInventoryRows] = useState([]);
  const [accountRows, setAccountRows] = useState([]);
  const [inventoryBaseMap, setInventoryBaseMap] = useState({});
  const [accountBaseMap, setAccountBaseMap] = useState({});
  const [inventoryDirty, setInventoryDirty] = useState({});
  const [accountDirty, setAccountDirty] = useState({});
  const [inventorySearch, setInventorySearch] = useState("");
  const [accountSearch, setAccountSearch] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setMe(null);
        setCompanyId(null);
        setLoadingMe(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const data = snap.exists() ? { id: snap.id, uid: user.uid, ...snap.data() } : { uid: user.uid };
        setMe(data);
        setCompanyId(data?.companyId || null);
      } catch (error) {
        console.error("[GridsScreen] Failed loading user:", error);
        setMe({ uid: user.uid });
        setCompanyId(null);
      } finally {
        setLoadingMe(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!companyId) return undefined;
    const unsubscribe = onSnapshot(
      doc(db, "companies", companyId),
      (snap) => setCompanySettings(snap.exists() ? snap.data() || { featureFlags: {} } : { featureFlags: {} }),
      (error) => {
        console.warn("[GridsScreen] Company settings listener failed:", error);
        setCompanySettings({ featureFlags: {} });
      }
    );
    return () => unsubscribe();
  }, [companyId]);

  const role = normalizeRole(me?.role);
  const gridsVisibility = normalizeGridsVisibility(companySettings?.featureFlags?.gridsVisibility);
  const canAccessGrids = useMemo(
    () => canViewGrids({ role, featureFlags: companySettings?.featureFlags || {} }),
    [role, companySettings?.featureFlags]
  );
  const isAdminOrOwner = role === "owner" || role === "admin" || role === "super_admin";
  const canEditInventoryGrid = isAdminOrOwner;
  const canEditAccountsGrid = isAdminOrOwner || role === "enduser" || role === "end_user";

  useEffect(() => {
    if (forcedTab && forcedTab !== activeTab) {
      setActiveTab(forcedTab);
    }
  }, [forcedTab, activeTab]);

  useEffect(() => {
    if (!companyId || !canAccessGrids) return undefined;

    const loadMeta = async () => {
      try {
        const [usersSnap, territoriesSnap] = await Promise.all([
          getDocs(query(collection(db, "users"), where("companyId", "==", companyId))),
          getDocs(collection(db, "companies", companyId, "territories")),
        ]);

        const users = usersSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((user) => normalizeRole(user.role) !== "customer");
        const territoryList = territoriesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));

        setTeamUsers(users);
        setTerritories(territoryList);
      } catch (error) {
        console.error("[GridsScreen] Failed loading grid metadata:", error);
      }
    };

    loadMeta();
    return undefined;
  }, [companyId, canAccessGrids]);

  const loadInventory = useCallback(async () => {
    if (Array.isArray(inventoryItems)) {
      const visible = isAdminOrOwner
        ? inventoryItems
        : inventoryItems.filter((item) =>
            uniqueStrings(item.groupIds).some((groupId) => uniqueStrings(me?.groupIds).includes(groupId))
          );

      const baseMap = {};
      const rows = visible
        .filter((item) => item.archived !== true)
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
        .map((item) => {
          const row = {
            id: item.id,
            name: item.name || "",
            sku: item.sku || "",
            brand: item.brand || "",
            category: item.category || "",
            packSize: item.packSize || "",
            price: item.price ?? "",
            stock: item.stock ?? 0,
            sampleable: item.sampleable !== false && item.unsampleable !== true,
            customerVisible: item.customerVisible !== false && item.hiddenFromCustomer !== true,
          };
          baseMap[item.id] = { raw: item, row };
          return row;
        });

      setInventoryBaseMap(baseMap);
      setInventoryRows(rows);
      setInventoryDirty({});
      setLoadingInventory(false);
      return;
    }

    if (!companyId || !canAccessGrids) {
      setInventoryRows([]);
      setInventoryBaseMap({});
      setLoadingInventory(false);
      return;
    }

    setLoadingInventory(true);
    try {
      const snap = await getDocs(collection(db, "companies", companyId, "inventory"));
      const raw = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      const visible = isAdminOrOwner
        ? raw
        : raw.filter((item) => uniqueStrings(item.groupIds).some((groupId) => uniqueStrings(me?.groupIds).includes(groupId)));

      const baseMap = {};
      const rows = visible
        .filter((item) => item.archived !== true)
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
        .map((item) => {
          const row = {
            id: item.id,
            name: item.name || "",
            sku: item.sku || "",
            brand: item.brand || "",
            category: item.category || "",
            packSize: item.packSize || "",
            price: item.price ?? "",
            stock: item.stock ?? 0,
            sampleable: item.sampleable !== false && item.unsampleable !== true,
            customerVisible: item.customerVisible !== false && item.hiddenFromCustomer !== true,
          };
          baseMap[item.id] = { raw: item, row };
          return row;
        });

      setInventoryBaseMap(baseMap);
      setInventoryRows(rows);
      setInventoryDirty({});
    } catch (error) {
      console.error("[GridsScreen] Failed loading inventory:", error);
      setInventoryRows([]);
      setInventoryBaseMap({});
    } finally {
      setLoadingInventory(false);
    }
  }, [companyId, canAccessGrids, inventoryItems, isAdminOrOwner, me?.groupIds]);

  const loadAccounts = useCallback(async () => {
    if (!companyId || !canAccessGrids) {
      setAccountRows([]);
      setAccountBaseMap({});
      setLoadingAccounts(false);
      return;
    }

    setLoadingAccounts(true);
    try {
      const colRef = collection(db, "companies", companyId, "accounts");
      let docs = [];

      if (isAdminOrOwner) {
        const snap = await getDocs(colRef);
        docs = snap.docs;
      } else {
        const myUid = me?.uid || me?.id || "";
        const results = await Promise.allSettled([
          getDocs(query(colRef, where("assignedToId", "==", myUid))),
          getDocs(query(colRef, where("assignedUserId", "==", myUid))),
          getDocs(query(colRef, where("repUserId", "==", myUid))),
          getDocs(query(colRef, where("salesRepId", "==", myUid))),
        ]);

        const deduped = new Map();
        results.forEach((result) => {
          if (result.status !== "fulfilled") return;
          result.value.docs.forEach((docSnap) => deduped.set(docSnap.id, docSnap));
        });
        docs = Array.from(deduped.values());
      }

      const baseMap = {};
      const rows = docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => String(a.name || a.businessName || "").localeCompare(String(b.name || b.businessName || "")))
        .map((account) => {
          const row = {
            id: account.id,
            name: account.name || account.businessName || "",
            contactName: account.contactName || account.primaryContact?.name || "",
            contactEmail: account.contactEmail || account.primaryContact?.email || account.email || "",
            phone: account.phone || account.primaryContact?.phone || "",
            assignedToId: account.assignedToId || account.assignedUserId || "",
            territoryId: account.territoryId || "",
            stage: account.stage || "Prospect",
            active: account.active !== false,
          };
          baseMap[account.id] = { raw: account, row };
          return row;
        });

      setAccountBaseMap(baseMap);
      setAccountRows(rows);
      setAccountDirty({});
    } catch (error) {
      console.error("[GridsScreen] Failed loading accounts:", error);
      setAccountRows([]);
      setAccountBaseMap({});
    } finally {
      setLoadingAccounts(false);
    }
  }, [companyId, canAccessGrids, isAdminOrOwner, me?.id, me?.uid]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const updateInventoryCell = (rowId, field, value) => {
    if (!canEditInventoryGrid) return;
    setInventoryRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)));
    setInventoryDirty((prev) => ({ ...prev, [rowId]: { ...(prev[rowId] || {}), [field]: value } }));
  };

  const updateAccountCell = (rowId, field, value) => {
    if (!canEditAccountsGrid) return;
    setAccountRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)));
    setAccountDirty((prev) => ({ ...prev, [rowId]: { ...(prev[rowId] || {}), [field]: value } }));
  };

  const visibleInventoryRows = useMemo(() => {
    const term = inventorySearch.trim().toLowerCase();
    if (!term) return inventoryRows;
    return inventoryRows.filter((row) =>
      [row.name, row.sku, row.brand, row.category, row.packSize].some((value) =>
        String(value || "").toLowerCase().includes(term)
      )
    );
  }, [inventoryRows, inventorySearch]);

  const visibleAccountRows = useMemo(() => {
    const term = accountSearch.trim().toLowerCase();
    if (!term) return accountRows;
    return accountRows.filter((row) =>
      [row.name, row.contactName, row.contactEmail, row.phone].some((value) =>
        String(value || "").toLowerCase().includes(term)
      )
    );
  }, [accountRows, accountSearch]);

  const teamUserOptions = useMemo(
    () =>
      teamUsers
        .map((user) => ({
          value: user.id,
          label: user.fullName || user.displayName || user.email || "Unnamed user",
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teamUsers]
  );

  const territoryOptions = useMemo(
    () =>
      territories
        .map((territory) => ({
          value: territory.id,
          label: territory.name || territory.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [territories]
  );

  const dirtyInventoryCount = Object.keys(inventoryDirty).length;
  const dirtyAccountCount = Object.keys(accountDirty).length;
  const totalDirtyCount = dirtyInventoryCount + dirtyAccountCount;

  const revertAllChanges = () => {
    setInventoryRows(Object.values(inventoryBaseMap).map((entry) => entry.row));
    setAccountRows(Object.values(accountBaseMap).map((entry) => entry.row));
    setInventoryDirty({});
    setAccountDirty({});
    setSaveError("");
  };

  const saveInventoryChanges = async () => {
    if (!canEditInventoryGrid) return;
    const dirtyEntries = Object.entries(inventoryDirty);
    if (!dirtyEntries.length) return;

    const skuOwnerMap = new Map();
    inventoryRows.forEach((row) => {
      const currentSku = normalizeSku(row?.sku);
      if (currentSku && !skuOwnerMap.has(currentSku)) skuOwnerMap.set(currentSku, row.id);

      uniqueStrings(row?.previousSkus).forEach((previousSku) => {
        const previousSkuNorm = normalizeSku(previousSku);
        if (previousSkuNorm && !skuOwnerMap.has(previousSkuNorm)) skuOwnerMap.set(previousSkuNorm, row.id);
      });
    });

    for (const [rowId] of dirtyEntries) {
      const row = inventoryRows.find((item) => item.id === rowId);
      const base = inventoryBaseMap[rowId];
      if (!row || !base) continue;

      const nextSkuNorm = normalizeSku(row?.sku);
      const baseSkuNorm = normalizeSku(base?.sku);
      if (!nextSkuNorm || nextSkuNorm === baseSkuNorm) continue;

      const ownerId = skuOwnerMap.get(nextSkuNorm);
      if (ownerId && ownerId !== rowId) {
        throw new Error(`SKU "${row.sku}" already exists on another product or in its SKU history.`);
      }
      skuOwnerMap.set(nextSkuNorm, rowId);
    }

    const chunks = batchInChunks(dirtyEntries);
    for (const chunk of chunks) {
      const batch = writeBatch(db);
      chunk.forEach(([rowId]) => {
        const row = inventoryRows.find((item) => item.id === rowId);
        const base = inventoryBaseMap[rowId];
        if (!row || !base) return;

        const payload = {
          name: String(row.name || "").trim(),
          sku: String(row.sku || "").trim() || null,
          brand: String(row.brand || "").trim() || null,
          category: String(row.category || "").trim() || null,
          packSize: String(row.packSize || "").trim() || null,
          price: row.price === "" ? null : Number(row.price),
          stock: row.stock === "" ? 0 : Number(row.stock),
          sampleable: !!row.sampleable,
          unsampleable: !row.sampleable,
          customerVisible: !!row.customerVisible,
          hiddenFromCustomer: !row.customerVisible,
          updatedAt: serverTimestamp(),
          updatedBy: me?.uid || me?.id || null,
        };

        if (!payload.sku) {
          throw new Error("Inventory items in grids still need a SKU.");
        }
        if (!payload.name) {
          throw new Error("Inventory items in grids still need a product name.");
        }

        const baseSku = String(base.sku || "").trim();
        const nextSku = String(payload.sku || "").trim();
        const baseSkuNorm = normalizeSku(baseSku);
        const nextSkuNorm = normalizeSku(nextSku);
        if (baseSkuNorm && nextSkuNorm !== baseSkuNorm) {
          payload.previousSkus = uniqueStrings([...(Array.isArray(base.previousSkus) ? base.previousSkus : []), baseSku]);
        }

        batch.update(doc(db, "companies", companyId, "inventory", rowId), payload);

        const comparableBase = {
          name: String(base.name || "").trim(),
          sku: String(base.sku || "").trim() || null,
          brand: String(base.brand || "").trim() || null,
          category: String(base.category || "").trim() || null,
          packSize: String(base.packSize || "").trim() || null,
          previousSkus: uniqueStrings(base.previousSkus),
          price: base.price === "" || base.price == null ? null : Number(base.price),
          stock: base.stock === "" || base.stock == null ? 0 : Number(base.stock),
          sampleable: !!base.sampleable,
          customerVisible: !!base.customerVisible,
        };

        const comparableAfter = {
          ...comparableBase,
          ...payload,
          previousSkus: Array.isArray(payload.previousSkus)
            ? uniqueStrings(payload.previousSkus)
            : comparableBase.previousSkus,
        };

        const changedFields = Object.keys(comparableBase).filter((field) => {
          const beforeValue = comparableBase[field];
          const afterValue = comparableAfter[field];
          return beforeValue !== afterValue;
        });

        if (changedFields.length > 0) {
          const changes = changedFields.reduce((acc, field) => {
            acc[field] = {
              from: comparableBase[field],
              to: comparableAfter[field],
            };
            return acc;
          }, {});

          const changelogRef = doc(collection(db, "companies", companyId, "inventory", rowId, "changelog"));
          batch.set(changelogRef, {
            changedAt: Timestamp.now(),
            changedBy: me?.uid || me?.id || null,
            type: "updatedViaGrid",
            changedFields,
            changes,
            priceChange: changedFields.includes("price")
              ? {
                  from: comparableBase.price,
                  to: payload.price,
                }
              : null,
            source: "gridsInventory",
          });

          if (changedFields.includes("price")) {
            const priceChangeDetail = buildInventoryPriceChangeDetail({
              companyId,
              inventoryId: rowId,
              name: payload.name,
              sku: payload.sku,
              category: payload.category,
              changedBy: me?.uid || me?.id || null,
              changedByLabel: me?.fullName || me?.displayName || me?.email || null,
              source: "gridsInventory",
              type: "updatedViaGrid",
              from: comparableBase.price,
              to: payload.price,
            });
            const { ref: priceFeedRef, payload: priceFeedPayload } = createInventoryPriceChangeWrite({
              db,
              companyId,
              detail: priceChangeDetail,
            });
            batch.set(priceFeedRef, priceFeedPayload);
          }

        }
      });
      await batch.commit();
    }
  };

  const saveAccountChanges = async () => {
    if (!canEditAccountsGrid) return;
    const dirtyEntries = Object.entries(accountDirty);
    if (!dirtyEntries.length) return;

    const teamUserById = new Map(teamUsers.map((user) => [user.id, user]));
    const territoryById = new Map(territories.map((territory) => [territory.id, territory]));

    const chunks = batchInChunks(dirtyEntries);
    for (const chunk of chunks) {
      const batch = writeBatch(db);
      chunk.forEach(([rowId]) => {
        const row = accountRows.find((item) => item.id === rowId);
        const base = accountBaseMap[rowId];
        if (!row || !base) return;

        const assignedUser = row.assignedToId ? teamUserById.get(row.assignedToId) : null;
        const territory = row.territoryId ? territoryById.get(row.territoryId) : null;
        const trimmedContactEmail = String(row.contactEmail || "").trim().toLowerCase();
        const trimmedName = String(row.name || "").trim();

        if (!trimmedName) {
          throw new Error("Accounts in grids still need a location name.");
        }
        if (row.active !== false && !trimmedContactEmail) {
          throw new Error("Active accounts in grids still need a contact email.");
        }

        const payload = {
          name: trimmedName,
          contactName: String(row.contactName || "").trim() || null,
          contact: String(row.contactName || "").trim() || null,
          email: trimmedContactEmail || null,
          contactEmail: trimmedContactEmail || null,
          phone: String(row.phone || "").trim() || null,
          primaryContact: {
            ...(base.raw.primaryContact || {}),
            name: String(row.contactName || "").trim() || null,
            email: trimmedContactEmail || null,
            phone: String(row.phone || "").trim() || null,
          },
          assignedToId: row.assignedToId || null,
          assignedUserId: row.assignedToId || null,
          assignedToName: assignedUser
            ? assignedUser.fullName || assignedUser.displayName || assignedUser.email || null
            : null,
          territoryId: row.territoryId || null,
          territoryName: territory ? territory.name || territory.id : null,
          territoryOverride: !!row.territoryId,
          stage: row.stage || "Prospect",
          active: row.active !== false,
          updatedAt: serverTimestamp(),
        };

        batch.update(doc(db, "companies", companyId, "accounts", rowId), payload);
      });
      await batch.commit();
    }
  };

  const saveAllChanges = async () => {
    if (!totalDirtyCount || saving) return;
    setSaving(true);
    setSaveError("");
    try {
      await saveInventoryChanges();
      await saveAccountChanges();
      await Promise.all([loadInventory(), loadAccounts()]);
      if (typeof onInventorySaved === "function") {
        await onInventorySaved();
      }
    } catch (error) {
      console.error("[GridsScreen] Failed saving grids:", error);
      setSaveError(error?.message || "Could not save grid changes.");
    } finally {
      setSaving(false);
    }
  };

  if (loadingMe) {
    return <div className="px-4 py-8 text-sm text-slate-500">Loading grids…</div>;
  }

  if (!companyId) {
    return <div className="px-4 py-8 text-sm text-slate-500">No company found for this user.</div>;
  }

  if (!canAccessGrids) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
        <div className="font-semibold text-slate-900">Grids are not available for this role.</div>
        <div className="mt-1">
          Current visibility: <span className="font-medium">{gridsVisibility.replaceAll("_", " ")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "w-full max-w-none px-0 py-0" : "w-full max-w-none px-0 py-1"}>
      <div className={embedded ? "overflow-hidden bg-white" : "overflow-hidden bg-white md:rounded-2xl md:border md:border-slate-200 md:shadow-lg"}>
        <div className="border-b border-slate-200">
          <div className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:px-6 md:py-0">
            {showTabNav ? (
              <nav className="flex overflow-x-auto text-sm">
                {[
                  { id: "inventory", label: "Inventory Grid", Icon: Package2 },
                  { id: "accounts", label: "Accounts Grid", Icon: Building2 },
                ].map(({ id, label, Icon }) => {
                  const active = activeTab === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveTab(id)}
                      className={`relative flex items-center gap-2 px-6 py-4 font-medium transition-all ${
                        active ? "bg-blue-50 text-blue-600" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{label}</span>
                      {active ? <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" /> : null}
                    </button>
                  );
                })}
              </nav>
            ) : (
              <div className="py-3 text-sm font-semibold text-slate-900">
                {activeTab === "accounts" ? "Accounts Grid" : "Inventory Grid"}
              </div>
            )}

            {!embedded ? (
              <div className={`flex flex-wrap items-center gap-2 ${showTabNav ? "md:ml-auto" : "md:ml-auto md:py-3"}`}>
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-medium text-blue-700">
                  <Database className="h-3.5 w-3.5" />
                  {totalDirtyCount} pending change{totalDirtyCount === 1 ? "" : "s"}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    loadInventory();
                    loadAccounts();
                  }}
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={revertAllChanges}
                  disabled={!totalDirtyCount || saving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Revert
                </button>
                <button
                  type="button"
                  onClick={saveAllChanges}
                  disabled={!totalDirtyCount || saving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Saving…" : "Save All Changes"}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="p-3 md:p-6">
          {saveError ? (
            <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <AlertCircle className="h-4 w-4" />
              {saveError}
            </div>
          ) : null}

          {activeTab === "inventory" ? (
            <GridShell
              title={embedded ? null : "Inventory Grid"}
              icon={Package2}
              description={
                embedded
                  ? null
                  : canEditInventoryGrid
                  ? "Excel-style bulk editing for your core inventory fields."
                  : "Inventory grid follows normal inventory permissions. You can view this data here, but only admins and owners can edit it."
              }
              search={inventorySearch}
              onSearchChange={setInventorySearch}
              emptyText={loadingInventory ? "Loading inventory…" : "No inventory items available for this grid."}
              rightActions={
                activeTab === "inventory" ? (
                  <div className="flex flex-wrap items-center gap-2 md:justify-end">
                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-medium text-blue-700">
                      <Database className="h-3.5 w-3.5" />
                      {totalDirtyCount} pending change{totalDirtyCount === 1 ? "" : "s"}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        loadInventory();
                        loadAccounts();
                      }}
                      className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Refresh
                    </button>
                    <button
                      type="button"
                      onClick={revertAllChanges}
                      disabled={!totalDirtyCount || saving}
                      className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Revert
                    </button>
                    <button
                      type="button"
                      onClick={saveAllChanges}
                      disabled={!totalDirtyCount || saving}
                      className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? "Saving…" : "Save All Changes"}
                    </button>
                  </div>
                ) : null
              }
            >
              {loadingInventory ? (
                <div className="px-4 py-10 text-sm text-slate-500">Loading inventory…</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-[980px] w-full border-collapse text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      <tr>
                        {["Name", "SKU", "Pack Size", "Price", "Stock", "Sample-able", "Customer Visible"].map((label) => (
                          <th key={label} className="border-b border-slate-200 px-3 py-2 font-semibold">
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleInventoryRows.map((row) => (
                        <tr key={row.id} className={inventoryDirty[row.id] ? "bg-blue-50/40" : "bg-white"}>
                          <td className="border-b border-slate-200 px-2 py-1 min-w-[320px]"><InventoryCell value={row.name} readOnly={!canEditInventoryGrid} onChange={(value) => updateInventoryCell(row.id, "name", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><InventoryCell value={row.sku} readOnly={!canEditInventoryGrid} onChange={(value) => updateInventoryCell(row.id, "sku", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><InventoryCell value={row.packSize} readOnly={!canEditInventoryGrid} onChange={(value) => updateInventoryCell(row.id, "packSize", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><InventoryCell type="number" step="0.01" readOnly={!canEditInventoryGrid} value={row.price} onChange={(value) => updateInventoryCell(row.id, "price", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><InventoryCell type="number" step="1" readOnly={!canEditInventoryGrid} value={row.stock} onChange={(value) => updateInventoryCell(row.id, "stock", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><CheckboxCell checked={row.sampleable} disabled={!canEditInventoryGrid} onChange={(value) => updateInventoryCell(row.id, "sampleable", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><CheckboxCell checked={row.customerVisible} disabled={!canEditInventoryGrid} onChange={(value) => updateInventoryCell(row.id, "customerVisible", value)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </GridShell>
          ) : (
            <GridShell
              title="Accounts Grid"
              icon={Building2}
              description={
                canEditAccountsGrid
                  ? "Spreadsheet-style updates for key account ownership and contact fields."
                  : "Accounts grid follows normal account permissions."
              }
              search={accountSearch}
              onSearchChange={setAccountSearch}
              emptyText={loadingAccounts ? "Loading accounts…" : "No accounts available for this grid."}
            >
              {loadingAccounts ? (
                <div className="px-4 py-10 text-sm text-slate-500">Loading accounts…</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-[1200px] w-full border-collapse text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      <tr>
                        {["Location Name", "Contact Name", "Contact Email", "Phone", "Assigned Rep", "Territory", "Stage", "Active"].map((label) => (
                          <th key={label} className="border-b border-slate-200 px-3 py-2 font-semibold">
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleAccountRows.map((row) => (
                        <tr key={row.id} className={accountDirty[row.id] ? "bg-blue-50/40" : "bg-white"}>
                          <td className="border-b border-slate-200 px-2 py-1"><InventoryCell value={row.name} onChange={(value) => updateAccountCell(row.id, "name", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><InventoryCell value={row.contactName} onChange={(value) => updateAccountCell(row.id, "contactName", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><InventoryCell type="email" value={row.contactEmail} onChange={(value) => updateAccountCell(row.id, "contactEmail", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><InventoryCell value={row.phone} onChange={(value) => updateAccountCell(row.id, "phone", value)} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><SelectCell value={row.assignedToId} onChange={(value) => updateAccountCell(row.id, "assignedToId", value)} options={teamUserOptions} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><SelectCell value={row.territoryId} onChange={(value) => updateAccountCell(row.id, "territoryId", value)} options={territoryOptions} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><SelectCell value={row.stage} onChange={(value) => updateAccountCell(row.id, "stage", value)} options={ACCOUNT_STAGES.map((stage) => ({ value: stage, label: stage }))} /></td>
                          <td className="border-b border-slate-200 px-2 py-1"><CheckboxCell checked={row.active} onChange={(value) => updateAccountCell(row.id, "active", value)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </GridShell>
          )}
        </div>
      </div>
    </div>
  );
}
